import { styled } from '@mui/material';
import { cloneElement, useState } from 'react';
import Box from 'shell/components/Box';
import Icon from 'shell/components/Icon';
import Menu from 'shell/components/Menu';
import MenuItem from 'shell/components/MenuItem';
import Tooltip from 'shell/components/Tooltip';

const ALIGN = ['left', 'right'];

const CustomMenuItem = styled(MenuItem)(({ destructive, disabled, theme }) => {
    return {
        gap: theme.spacing(1),
        '&.Mui-focusVisible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '-5px',
        },
        ...(destructive && {
            color: theme.palette.ensightful.error[600],
        }),
        ...(disabled && {
            background: 'none !important',
            pointerEvents: 'auto !important',
        }),
    };
});

const Dropdown = ({ id, align = 'right', options, rootComponent, onSelect, stayOpen = false, ...rest }) => {
    if (!id) throw new Error('Shell: Missing id in component Dropdown.');
    if (!ALIGN.includes(align)) throw new Error(`Shell: Invalid align "${align}" in component Dropdown.`);

    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (e) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
        if (rootComponent.props.onClick) rootComponent.props.onClick(e);
    };

    const handleClose = (e) => {
        if (e) e.stopPropagation();
        setAnchorEl(null);
    };

    const handleItemClick = (e, { key, value }) => {
        e.stopPropagation();
        if (!stayOpen) {
            setAnchorEl(null);
        }
        if (key && value) onSelect?.({ key, value });
    };

    const open = Boolean(anchorEl);

    return (
        <Box {...rest}>
            {cloneElement(rootComponent, {
                id,
                'aria-controls': open ? `${id}-menu` : undefined,
                'aria-expanded': open ? true : undefined,
                'aria-haspopup': true,
                onClick: handleClick,
            })}
            <Menu
                id={`${id}-menu`}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: align,
                }}
                MenuListProps={{
                    'aria-labelledby': id,
                }}
                open={open}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: align,
                }}
                onClick={(e) => e.stopPropagation()}
                onClose={handleClose}
            >
                {options.map(({ key, value, disabled, icon, tooltip }) =>
                    tooltip ? (
                        <Tooltip key={key} enterDelay={100} maxWidth={200} title={tooltip} placement={'left'}>
                            <CustomMenuItem
                                destructive={key === 'delete'}
                                disabled={disabled}
                                onClick={disabled ? undefined : (e) => handleItemClick(e, { key, value })}
                            >
                                {icon && <Icon kind={icon} />}
                                {value}
                            </CustomMenuItem>
                        </Tooltip>
                    ) : (
                        <CustomMenuItem
                            key={key}
                            destructive={key === 'delete'}
                            disabled={disabled}
                            onClick={disabled ? undefined : (e) => handleItemClick(e, { key, value })}
                        >
                            {icon && <Icon kind={icon} />}
                            {value}
                        </CustomMenuItem>
                    )
                )}
            </Menu>
        </Box>
    );
};

export default Dropdown;
